'use client';
import { gql } from '@apollo/client';
import type { BrandReportState2, ReportMetric } from '@mnd-frontend/graphql-schema';
import type { UntypedFunction } from '@mnd-frontend/ts';
import { createContext, type ReactNode, useContext } from 'react';
import {
  useReport2UpdateMutation,
  useReport2UpdatePrValueSettingsMutation,
} from '../../../generated/hooks';
import type { BrandReportFragment, Report2PrValueSettingFragment } from '../../../generated/types';

export const REPORT2_META_FRAGMENT = gql`
  fragment Report2Meta on ReportInterface {
    id
    title
  }
`;

gql`
  mutation Report2Update(
    $id: ID!
    $title: String
    $description: String
    $brandColor: String
    $logotypeId: ID
    $coverImageId: ID
  ) {
    reportUpdate(
      input: {
        reportId: $id
        title: $title
        description: $description
        brandColor: $brandColor
        logotypeId: $logotypeId
        coverImageId: $coverImageId
      }
    ) {
      error {
        ... on ReportUpdateValidationError {
          title
          logotypeId
        }
      }
      report: report2 {
        ... on ReportInterface {
          id
          title
          brandColor
          description
          coverImage {
            id
            url(transformation: LIMIT_1000)
          }
          logotype {
            id
            url(transformation: LIMIT_1000)
          }
        }
      }
    }
  }

  mutation Report2UpdatePrValueSettings(
    $reportId: ID!
    $currency: ReportPrValueCurrency!
    $cpc: Float
    $cpm: Float
    $twitterCpm: Float
    $facebookCpm: Float
    $instagramCpm: Float
    $default: Boolean
    $pricingModel: ReportPrValuePricingModel
  ) {
    reportUpdatePrValueSettings(
      input: {
        reportId: $reportId
        cpc: $cpc
        cpm: $cpm
        twitterCpm: $twitterCpm
        facebookCpm: $facebookCpm
        instagramCpm: $instagramCpm
        currency: $currency
        default: $default
        pricingModel: $pricingModel
      }
    ) {
      error {
        message
      }
      report: report2 {
        id
        prValueSetting {
          ...Report2PrValueSetting
        }
        sections2 {
          ... on ReportSummarySection {
            subSections {
              ...Report2SummaryKeyMetrics
            }
          }
        }
      }
    }
  }
`;

type Props = {
  title: string;
  description: string | null;
  brandColor: string;
  logo?: BrandReportFragment['logotype'];
  coverImage?: BrandReportFragment['coverImage'];
  visibleMetrics: ReportMetric[];
  prValueSetting?: Report2PrValueSettingFragment;
  state?: BrandReportState2;
};

type ReportDynamicDataContextProps = Props & {
  updateReport: ReturnType<typeof useReport2UpdateMutation>[0];
  updatePrValueSettings: ReturnType<typeof useReport2UpdatePrValueSettingsMutation>[0];
};

const defaultValue = {
  title: '',
  description: '',
  brandColor: '',
  logo: undefined,
  coverImage: undefined,
  visibleMetrics: [],
  prValueSetting: {} as Report2PrValueSettingFragment,
  updateReport: (() => {}) as UntypedFunction, // eslint-disable-line @typescript-eslint/no-empty-function
  updatePrValueSettings: (() => {}) as UntypedFunction, // eslint-disable-line @typescript-eslint/no-empty-function
  state: undefined,
};

const ReportDynamicDataContext = createContext<ReportDynamicDataContextProps>(defaultValue);

export const ReportDynamicDataContextProvider = ({
  children,
  title,
  description,
  brandColor,
  logo,
  coverImage,
  visibleMetrics,
  prValueSetting,
  state,
}: Props & {
  children: ReactNode;
}) => {
  // TODO - check loading and error, maybe trigger notice
  const [updateReport] = useReport2UpdateMutation();
  const [updatePrValueSettings] = useReport2UpdatePrValueSettingsMutation();

  return (
    <ReportDynamicDataContext.Provider
      value={{
        brandColor,
        title,
        description,
        logo,
        coverImage,
        visibleMetrics,
        prValueSetting,
        updateReport,
        updatePrValueSettings,
        state,
      }}
    >
      {children}
    </ReportDynamicDataContext.Provider>
  );
};

export const useReportDynamicData = () => {
  const context = useContext(ReportDynamicDataContext);
  if (context === defaultValue) {
    throw new Error('useReportDynamicData must be used within a ReportDynamicDataContextProvider');
  }
  return context;
};
